import clsx from "clsx";
import NextLink from "next/link";
import { ComponentPropsWithoutRef, ElementType } from "react";
import { twMerge } from "tailwind-merge";
import { Image } from "components/Image/Image";
import { Tag } from "components/Tag";
import { ChevronRight } from "icons";
import { isBlockedPrefetchHref } from "lib/utils/isBlockedPrefetchUrl";
interface BrickProps<T extends ElementType> {
  title: string;
  subTitle?: string;
  imageUrlHash?: string;
  imageAlt?: string;
  imageSize?: "sm" | "md";
  titleTagLabel?: string;
  titleTagIconUrlHash?: string;
  titleTagIconAlt?: string;
  titleTagBgColor?: string;
  href?: string;
  hoverEffect?: boolean;
  isTab?: boolean;
  currentTab?: boolean;
  className?: string;
  as?: T;
}

type BrickExtendedProps<T extends ElementType> = BrickProps<T> & Omit<ComponentPropsWithoutRef<T>, keyof BrickProps<T>>;

const Brick = <T extends ElementType = "button">({
  href = "",
  title,
  subTitle = "",
  imageUrlHash = "",
  imageAlt = "",
  imageSize = "sm",
  titleTagLabel = "",
  titleTagIconUrlHash = "",
  titleTagIconAlt = "",
  titleTagBgColor = "",
  hoverEffect = true,
  currentTab = false,
  isTab = false,
  className = "",
  /* eslint-disable-next-line react/require-default-props */
  as,
  ...rest
}: BrickExtendedProps<T>) => {
  const tabClasses = clsx({
    "py-4": isTab,
    "transition-colors duration-200 ease-in-out hover:bg-fog-15 focus:bg-fog-15 active:bg-fog-15":
      hoverEffect && !currentTab,
    "transition-opacity duration-200 ease-in-out hover:opacity-70 focus:opacity-70 active:opacity-70":
      !hoverEffect && !currentTab,
    "bg-fog-00 drop-shadow-sm": currentTab && !href,
  });

  const brickTextPaddingClasses = clsx({
    "pr-0": !isTab,
  });

  const imageClasses = clsx({
    "h-10 min-w-10 max-w-10 ": imageSize === "sm",
    "h-16 min-w-16 max-w-16": imageSize === "md",
  });

  const determineCompType = () => {
    if (as) return as;

    if (!href) return "button";

    if (href && !isBlockedPrefetchHref(href)) return NextLink;

    return "a";
  };
  const Component = determineCompType();

  return (
    <Component
      className={twMerge("relative w-full rounded-md px-4 py-3 text-left", tabClasses, className)}
      {...(href ? { href } : {})}
      {...rest}
    >
      <div className="flex items-center gap-4">
        {imageUrlHash && (
          <Image
            src={imageUrlHash}
            alt={imageAlt}
            width={100}
            height={100}
            className={twMerge("object-cover object-center", imageClasses)}
          />
        )}
        <div
          className={twMerge(
            "lg:pr-unset inline-flex flex-col items-start justify-start gap-0.5 pr-10 lg:pr-0",
            brickTextPaddingClasses
          )}
        >
          <div className="inline-flex items-center justify-start gap-2">
            <div className="inline-flex gap-2 text-base font-semibold leading-snug text-fog-80">
              <p className="line-clamp-2 text-base">{title}</p>
              {titleTagLabel && (
                <Tag
                  bgColor={titleTagBgColor}
                  label={titleTagLabel}
                  iconUrlHash={titleTagIconUrlHash}
                  iconAlt={titleTagIconAlt}
                  className="fill-dusk-70 text-dusk-70"
                />
              )}
            </div>
          </div>
          <p className="text-sm text-fog-60">{subTitle}</p>
        </div>
      </div>
      {currentTab && !href && (
        <ChevronRight fill="current" className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2" />
      )}
    </Component>
  );
};

export { Brick };
